import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useTheme } from '@mui/styles';
import NodeOperator from './nodeoperator';
import NodeExpression from './nodeexpression';
import Box from '@mui/material/Box';
import { useDrag, useDrop } from 'react-dnd';
import { Collapse } from '@mui/material';
import PropTypes from 'prop-types';


const TreeView = ({ node, level = 0, onMove, onAdd, onDelete, onChange, onValidate, onToggleExpand, mappings, fields, path = [], isLastChild = false }) => {
    const [expanded, setExpanded] = useState(node.expanded || false);
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const [dropPosition, setDropPosition] = useState(null);
    const open = Boolean(anchorEl);
    const ref = useRef(null);
    const currentPath = [...path, node.id];
    const isGroupNode = Array.isArray(node.children);
    const isRoot = node.root === true;

    const handleToggle = () => setExpanded(!expanded);

    const [{ isDragging }, drag] = useDrag({
        type: 'TREE_ITEM',
        item: () => ({ id: node.id, level, isRoot, path }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    useEffect(() => {
        setExpanded(node.expanded || false);
    }, [node.expanded]);


    const [{ isOver, canDrop }, drop] = useDrop({
        accept: 'TREE_ITEM',
        hover(item, monitor) {
            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (hoverClientY < hoverMiddleY / 2) {
                // Upper quarter -> insert above
                setDropPosition('above');
            } else if (hoverClientY > hoverMiddleY + hoverMiddleY / 2) {
                // Lower quarter -> insert below
                setDropPosition('below');
            } else {
                // Middle -> insert into
                setDropPosition('inside');
            }
        },
        canDrop: (draggedNode) => {
            if (!isGroupNode) return false;
            if (draggedNode.isRoot) return false;
            if (draggedNode.id === node.id) return false;
            return true;
        },
        drop: (item) => {
            onMove(item.id, node.id); // pass position
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });


    drag(drop(ref));

    // Determine edge color during drag operation - only show green for valid drops
    const getEdgeColor = () => {
        if (isOver && canDrop) {
            return 'rgba(81, 171, 255, 1)';
        }
        return theme.palette.divider;
    };

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Tree Connecting Lines with dynamic colors */}
            {level > 0 && (
                <Box sx={[{
                    position: 'absolute',
                    left: (level * 30) + 16 + 'px',
                    top: 0,
                    bottom: isLastChild ? '42%' : 0,
                    height: isGroupNode && isLastChild ? '28px' : 'unset',
                    width: isOver && canDrop ? '2px' : '1px',
                    bgcolor: getEdgeColor(),
                    transition: 'background-color 0.3s, width 0.3s',
                    zIndex: 0
                }]}/>
            )}
            {level > 0 && (
                <Box sx={[{
                    position: 'absolute',
                    left: (level * 30) + 16 + 'px',
                    top: isGroupNode? '28px' : '30px',
                    width: '16px',
                    height: isOver && canDrop ? '2px' : '1px',
                    bgcolor: getEdgeColor(),
                    transition: 'background-color 0.3s, height 0.3s',
                    zIndex: 0
                }]} />
            )}

            <Box sx={{
                opacity: isDragging ? 0.5 : 1,
                position: 'relative',
                zIndex: 1
            }}>
                <Box
                    ref={ref}
                    sx={[{
                        display: 'flex',
                        alignItems: 'center',
                        pl: (level * 30) + 'px',
                        // py: isGroupNode ? 0 : 1,
                        //minHeight: !isGroupNode ? 50 : 0,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        cursor: isGroupNode ? 'default' : 'pointer',
                        borderRadius: '4px',
                    }]}
                    onClick={isGroupNode ? undefined : handleToggle}
                >
                    {!isGroupNode ? <NodeExpression node={node} fields={fields} onDelete={onDelete} onChange={onChange} mappings={mappings} onValidate={onValidate} /> : <NodeOperator node={node} handleAddnode={onAdd}
                        path={currentPath}
                        onDelete={() => onDelete(node.id)}
                        onToggleExpand={() => onToggleExpand(node.id)}
                        onChange={onChange} />}
                </Box>

                {isGroupNode && expanded && (
                    <Collapse in={expanded}>
                        <Box sx={{ position: 'relative' }}>
                            {node.children.map((child, index) => (
                                <TreeView
                                    key={child.id}
                                    node={child}
                                    level={level + 1}
                                    mappings={mappings}
                                    onMove={onMove}
                                    path={currentPath}
                                    onAdd={onAdd}
                                    onChange={onChange}
                                    onDelete={onDelete}
                                    onValidate={onValidate}
                                    onToggleExpand={onToggleExpand}
                                    fields={fields}
                                    isLastChild={index === node.children.length - 1}
                                />
                            ))}
                        </Box>
                    </Collapse>
                )}
            </Box>
        </Box>
    );
};

TreeView.propTypes = {
    node: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        expanded: PropTypes.bool,
        root: PropTypes.bool,
        children: PropTypes.array,
    }).isRequired,
    level: PropTypes.number,
    onMove: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    mappings: PropTypes.object.isRequired,
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    path: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    isLastChild: PropTypes.bool,
};

export default TreeView